import React, { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

// Import react bootstrap
import {
  Row,
  Col,
  Table,
  Card,
  FormControl,
  InputGroup,
  Button,
} from "react-bootstrap";

import { useDispatch, useSelector } from "react-redux";

// Import attendance components
import ActionForm from "components/attendance/ActionForm";
import ConfirmationDialog from "common/ConfirmationDialog";

// Import the HRPagination Component
import HRPagination from "components/HRPagination";

// Import Attendance API Slices
import { useGetAttendancesQuery } from "state/slices/attendance/attendanceApiSlice";

// Importing toast utilities
import { showErrorToast } from "utils/toast";

const trBackground = {
  late: "late",
  ot: "ot",
  wfh: "wfh",
  absent: "absent",
  half_day: "half-day",
  short_leave: "short-leave",
};

const ITEMS_PER_PAGE = 10;

const AttendanceViewManagement = ({
  allowedMerge = false,
  showDialog = false,
  handleConfirm = () => {},
  toggleDialog = () => {},
}) => {
  const { employeeId } = useParams();

  const currentDate = new Date();
  const attendanceMonth =
    currentDate.getMonth() !== 0 ? currentDate.getMonth() : 12;
  const attendanceYear =
    currentDate.getMonth() !== 0
      ? currentDate.getFullYear()
      : currentDate.getFullYear() - 1;

  // New state to track the selected month
  const [selectedMonthYear, setSelectedMonthYear] = useState({
    month: attendanceMonth,
    year: attendanceYear,
  });

  const [searchTerm, setSearchTerm] = useState(employeeId ? employeeId : "");
  const [exactMatch, setExactMatch] = useState(employeeId ? true : false);

  const [currentData, setCurrentData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch attendances and handle errors using the selected month
  const {
    data: attendancesData,
    isFetching: isFetchingAttendances,
    isError: isAttendancesError,
    error: attendancesError,
    refetch: refetchAttendances,
  } = useGetAttendancesQuery(selectedMonthYear);

  useEffect(() => {
    if (attendancesData && !isAttendancesError) {
      const flattenedData = Object.values(attendancesData).flat();
      const newData = flattenedData.map((data) => {
        const employeeId = Object.keys(attendancesData).find((key) =>
          attendancesData[key].includes(data)
        );
        return { ...data, employeeId };
      });

      const filteredData = newData
        ? newData.filter((item) =>
            Object.values(item).some((value) =>
              typeof value === "string" && exactMatch
                ? value.trim().toLowerCase() === searchTerm.toLocaleLowerCase()
                : value.trim().toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        : [];

      setTotalPages(Math.ceil(filteredData.length / ITEMS_PER_PAGE));
      const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
      const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
      setCurrentData(filteredData.slice(indexOfFirstItem, indexOfLastItem));
    } else {
      setCurrentData([]);
      setTotalPages(1);
    }
  }, [
    attendancesData,
    isAttendancesError,
    searchTerm,
    currentPage,
    selectedMonthYear,
  ]);

  useEffect(() => {
    // Error toast is automatically shown by baseQuery for query errors
    if (isAttendancesError) {
      // Error already handled by baseQuery
    }
  }, [isAttendancesError, attendancesError]);

  // Use useEffect to make API requests after selectedMonthYear has been updated
  useEffect(() => {
    refetchAttendances();
  }, [selectedMonthYear]);

  // Update the selected month when the form is submitted
  const handleActionsSubmit = async (values) => {
    setSelectedMonthYear(values);
    // setCurrentData([]);
    // setTotalPages(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset the current page to 1 when searching
  };

  const getStatusClass = (status, workedFrom) => {
    if (!status && !workedFrom) return "";
    const order = [
      "OT",
      "Absent",
      "Half Day",
      "Short Leave",
      "Late",
      "Miss Punched",
    ];

    let key1 = null;
    if (status) {
      key1 = order.find((key) => status.toLowerCase().split(", ").includes(key.toLowerCase()));
    }

    const key = order.includes(workedFrom) ? workedFrom : key1 ? key1 : "";

    return trBackground[key.toLowerCase().trim().split(" ").join("_")] || "";
  };

  return (
    <Card className="py-3">
      <Card.Body>
        <div className="d-md-flex justify-content-between align-items-center">
          <ActionForm
            onSubmit={handleActionsSubmit}
            attendanceYear={attendanceYear}
            attendanceMonth={attendanceMonth}
          />
          <ConfirmationDialog
            show={showDialog}
            onClose={toggleDialog}
            onConfirm={() =>
              handleConfirm({ selectedMonthYear, refetchAttendances })
            }
          />
          <Row className="flex-row-reverse flex-md-row mb-3">
            {allowedMerge && (
              <Col xs={5}>
                <Button
                  variant="outline-danger"
                  className="w-100"
                  onClick={toggleDialog}
                >
                  Merge Records
                </Button>
              </Col>
            )}

            <Col xs={allowedMerge ? 7 : 12}>
              <InputGroup>
                <FormControl
                  type="text"
                  className="shadow-none border border-1"
                  placeholder="Search Here..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </InputGroup>
            </Col>
          </Row>
        </div>
        <Table striped bordered hover responsive className="text-center">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Employee Id</th>
              <th>Date</th>
              <th>Day</th>
              <th>Punch In</th>
              <th>Punch Out</th>
              <th>Worked-From</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 && !isFetchingAttendances ? (
              currentData.map((item, index) => (
                <tr
                  key={index}
                  className={getStatusClass(item.status, item.workedFrom)}
                >
                  <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                  <td>{item.name}</td>
                  <td>{item.employeeId ? item.employeeId : "—"}</td>
                  <td>{item.date ? item.date : "—"}</td>
                  <td>{item.day ? item.day : "—"}</td>
                  <td>{item.timeIn ? item.timeIn : "—"}</td>
                  <td>{item.timeOut ? item.timeOut : "—"}</td>
                  <td>{item.workedFrom ? item.workedFrom : "—"}</td>
                  <td>{item.status ? item.status : "—"}</td>
                </tr>
              ))
            ) : (
              <tr className="bg-light border text-center">
                <td colSpan={9} className="text-muted">
                  {isFetchingAttendances ? "Loading Data..." : "No Data Found"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {currentData.length > 0 && !isFetchingAttendances && (
          <div className="d-flex justify-content-end">
            <HRPagination
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AttendanceViewManagement;
